/**
 * Language Selector Component
 * Dropdown for switching between available languages
 */

import { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';

/**
 * Globe icon for language selector
 */
const GlobeIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

/**
 * Check icon for selected language
 */
const CheckIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

interface LanguageSelectorProps {
  /** When true, shows only the icon without the language code text */
  compact?: boolean;
}

export const LanguageSelector = ({ compact = false }: LanguageSelectorProps) => {
  const { theme } = useTheme();
  const { currentCulture, availableLanguages, setLanguage, isLoading, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Handle language selection
  const handleSelectLanguage = (cultureName: string) => {
    setLanguage(cultureName);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        style={{
          background: 'none',
          border: 'none',
          padding: '8px',
          cursor: isLoading ? 'wait' : 'pointer',
          color: theme.colors.textSecondary,
          display: 'flex',
          alignItems: 'center',
          gap: compact ? '0' : '6px',
          opacity: isLoading ? 0.6 : 1,
        }}
        title={`${t('Language')}: ${currentCulture.toUpperCase()}`}
      >
        <GlobeIcon size={20} />
        {!compact && (
          <span
            style={{
              fontSize: theme.typography.fontSizeSm,
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            {currentCulture.toUpperCase()}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            backgroundColor: theme.colors.backgroundCard,
            borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.dropdown,
            border: `1px solid ${theme.colors.border}`,
            minWidth: '180px',
            maxHeight: '320px',
            overflowY: 'auto',
            zIndex: 1000,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '8px 12px',
              borderBottom: `1px solid ${theme.colors.border}`,
              fontSize: theme.typography.fontSizeSm,
              fontWeight: theme.typography.fontWeightSemibold,
              color: theme.colors.textSecondary,
            }}
          >
            {t('SelectLanguage')}
          </div>

          {/* Language List */}
          {availableLanguages.map((lang) => {
            const isSelected = lang.cultureName === currentCulture;
            return (
              <button
                key={lang.cultureName}
                onClick={() => handleSelectLanguage(lang.cultureName)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  backgroundColor: isSelected ? theme.colors.brandAccentLight : 'transparent',
                  textAlign: 'left',
                  fontSize: theme.typography.fontSizeBase,
                  color: isSelected ? theme.colors.brandAccent : theme.colors.textPrimary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundMuted;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Language code badge */}
                  <span
                    style={{
                      fontSize: theme.typography.fontSizeXs,
                      fontWeight: theme.typography.fontWeightSemibold,
                      padding: '2px 6px',
                      borderRadius: theme.borderRadius.sm,
                      backgroundColor: isSelected
                        ? theme.colors.brandAccent
                        : theme.colors.borderLight,
                      color: isSelected ? 'white' : theme.colors.textSecondary,
                      minWidth: '28px',
                      textAlign: 'center',
                    }}
                  >
                    {lang.cultureName.toUpperCase()}
                  </span>
                  <span
                    style={{
                      fontWeight: isSelected
                        ? theme.typography.fontWeightSemibold
                        : theme.typography.fontWeightNormal,
                    }}
                  >
                    {lang.displayName}
                  </span>
                </div>
                {isSelected && (
                  <CheckIcon size={16} />
                )}
              </button>
            );
          })}

          {/* Empty state */}
          {availableLanguages.length === 0 && (
            <div
              style={{
                padding: '16px',
                textAlign: 'center',
                color: theme.colors.textMuted,
                fontSize: theme.typography.fontSizeSm,
              }}
            >
              {isLoading ? t('Loading') + '...' : t('NoLanguagesAvailable')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
